package com.ijoin.data.repository

import com.ijoin.data.api.AuthApi
import com.ijoin.data.model.AuthResponse
import com.ijoin.data.model.DeleteAccountRequest
import com.ijoin.data.model.LoginRequest
import com.ijoin.data.model.RegisterRequest
import com.ijoin.data.model.UpdateProfileRequest
import com.ijoin.data.model.User
import com.ijoin.data.push.PushTokenRegistrar
import com.ijoin.data.storage.TicketCache
import com.ijoin.data.storage.TokenManager

class AuthRepository {
    private val authApi = AuthApi()
    private val tokenManager = TokenManager()
    private val ticketCache = TicketCache()

    suspend fun register(
        email: String,
        username: String,
        password: String,
        firstName: String? = null,
        lastName: String? = null,
        phone: String? = null,
        avatarDataUrl: String? = null,
        language: String? = null,
        legalAccepted: Boolean = false,
        acceptedVersions: Map<String, String>? = null
    ): Result<AuthResponse> {
        val request = RegisterRequest(
            email = email,
            username = username,
            password = password,
            firstName = firstName,
            lastName = lastName,
            phone = phone,
            avatarDataUrl = avatarDataUrl,
            language = language,
            legalAccepted = legalAccepted,
            acceptedVersions = acceptedVersions
        )

        return authApi.register(request).also { result ->
            result.onSuccess { authResponse ->
                handleAuthSuccess(authResponse)
            }
        }
    }

    suspend fun login(email: String, password: String): Result<AuthResponse> {
        val request = LoginRequest(email = email, password = password)

        return authApi.login(request).also { result ->
            result.onSuccess { authResponse ->
                handleAuthSuccess(authResponse)
            }
        }
    }

    suspend fun refreshToken(): Result<AuthResponse> {
        val refreshToken = tokenManager.getRefreshToken()
            ?: return Result.failure(Exception("No refresh token available"))
        
        return authApi.refreshToken(refreshToken).also { result ->
            result.onSuccess { authResponse ->
                handleAuthSuccess(authResponse)
            }
        }
    }

    suspend fun exchangeGoogle(code: String): Result<AuthResponse> {
        return authApi.exchangeGoogle(code).also { result ->
            result.onSuccess { authResponse ->
                handleAuthSuccess(authResponse)
            }
        }
    }

    suspend fun exchangeApple(code: String): Result<AuthResponse> {
        return authApi.exchangeApple(code).also { result ->
            result.onSuccess { authResponse ->
                handleAuthSuccess(authResponse)
            }
        }
    }

    suspend fun logout(): Result<Unit> {
        val token = tokenManager.getToken()
        return if (token != null) {
            authApi.logout(token).also { result ->
                if (result.isSuccess) {
                    PushTokenRegistrar.unregister()
                }
                tokenManager.clearToken()
                tokenManager.clearRefreshToken()
                tokenManager.clearUserId()
                ticketCache.clear()
            }
        } else {
            PushTokenRegistrar.unregister()
            tokenManager.clearToken()
            tokenManager.clearRefreshToken()
            tokenManager.clearUserId()
            ticketCache.clear()
            Result.success(Unit)
        }
    }

    suspend fun getCurrentUser(): Result<User> {
        val token = tokenManager.getToken()
            ?: return Result.failure(Exception("Not authenticated"))

        return authApi.getCurrentUser(token)
    }

    fun isAuthenticated(): Boolean {
        return tokenManager.isAuthenticated()
    }

    fun getToken(): String? {
        return tokenManager.getToken()
    }

    fun getUserId(): Long? {
        return tokenManager.getUserId()
    }

    suspend fun updateProfile(
        username: String,
        firstName: String?,
        lastName: String?,
        phone: String?,
        avatarDataUrl: String?,
        language: String?
    ): Result<User> {
        val request = UpdateProfileRequest(
            username = username,
            firstName = firstName,
            lastName = lastName,
            phone = phone,
            avatarDataUrl = avatarDataUrl,
            language = language
        )
        return authApi.updateProfile(request)
    }

    suspend fun requestPasswordReset(email: String): Result<Unit> {
        return authApi.requestPasswordReset(email)
    }

    suspend fun validatePasswordResetToken(token: String): Result<Boolean> {
        return authApi.validatePasswordResetToken(token)
    }

    suspend fun resetPassword(token: String, newPassword: String): Result<Unit> {
        return authApi.confirmPasswordReset(token, newPassword)
    }

    suspend fun deleteAccount(): Result<Unit> {
        val userId = tokenManager.getUserId()
            ?: return Result.failure(Exception("Not authenticated"))

        return authApi.deleteAccount(DeleteAccountRequest(userId)).also { result ->
            if (result.isSuccess) {
                PushTokenRegistrar.unregister()
                tokenManager.clearToken()
                tokenManager.clearRefreshToken()
                tokenManager.clearUserId()
                ticketCache.clear()
            }
        }
    }

    private fun handleAuthSuccess(authResponse: AuthResponse) {
        clearTicketCacheIfUserChanged(authResponse.userId)
        tokenManager.saveToken(authResponse.token)
        authResponse.refreshToken?.let { tokenManager.saveRefreshToken(it) }
        tokenManager.saveUserId(authResponse.userId)
        PushTokenRegistrar.notifyAuthenticated()
    }

    private fun clearTicketCacheIfUserChanged(newUserId: Long?) {
        val previousUserId = tokenManager.getUserId()
        if (previousUserId == null || previousUserId != newUserId) {
            ticketCache.clear()
        }
    }
}
